import React, { useMemo } from "react";
import clsx from "clsx";
import {
  FingerPrintIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  CogIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { DataRecipientLogo } from "../../../components/DataRecipientLogo";
import {
  groupAndSortVendorsByCategory,
  getCategoryGroupOrder,
} from "../../../util/vendorCategorization";
import { ProcessingActivityDto } from "../../../../common/service/server/dto/ProcessingActivityDto";
import { labelForLawfulBasis } from "../../../hooks/useProcessingActivities";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";

type Source = {
  name: string;
  logoUrl?: string;
};

/** A collected category in the left card; `id` is present when the row can link to Step 1. */
export type DiagramCategory = {
  id?: string;
  name: string;
};

type DataFlowsDiagramProps = {
  org: OrganizationDto;
  picGroupName: string;
  categories: DiagramCategory[];
  sources: Source[];
  processingVendors: OrganizationDataRecipientDto[];
  /** Default and custom purposes alike — the diagram only needs a name and a deep-link id. */
  processingPurposes: { id: string; name: string }[];
  activities: ProcessingActivityDto[];
  showPurposes: boolean;
  /** Opens Step 1 focused on a category. Omit to render categories as plain text. */
  onCategoryClick?: (categoryId: string) => void;
  /** Opens Step 2 focused on a vendor. Omit to render vendors as plain text. */
  onVendorClick?: (vendorId: string) => void;
  /** Opens Step 3 focused on a purpose. Omit to render purposes as plain text. */
  onPurposeClick?: (purposeId: string) => void;
};

export function DataFlowsDiagram({
  org,
  picGroupName,
  categories,
  sources,
  processingVendors,
  processingPurposes,
  activities,
  showPurposes,
  onCategoryClick,
  onVendorClick,
  onPurposeClick,
}: DataFlowsDiagramProps) {
  const groupedVendors = useMemo(() => {
    return groupAndSortVendorsByCategory(processingVendors);
  }, [processingVendors]);

  const sortedGroupNames = useMemo(() => {
    return Object.keys(groupedVendors).sort(
      (a, b) => getCategoryGroupOrder(a) - getCategoryGroupOrder(b),
    );
  }, [groupedVendors]);

  let lawfulBases = Array.from(new Set(activities.flatMap((a) => a.lawfulBases)));
  const showLawfulBases = lawfulBases.length > 0 && org.featureGdpr;
  // Until purposes (or GDPR bases) exist the right card would be empty, leaving the connector
  // threads running into nothing — lay the diagram out as if purposes were off.
  const showPurposesColumn = showPurposes && (processingPurposes.length > 0 || showLawfulBases);

  return (
    <div
      className={clsx(
        "tw-boundary flex w-full items-center",
        !showPurposesColumn && "justify-center",
      )}
    >
      {/* Left card: collected categories + where they come from */}
      <DiagramCard style={{ width: 260 }} className="shrink-0 self-center">
        <div className="p-[16px]">
          <CardHeader
            icon={<FingerPrintIcon className="h-[16px] w-[16px]" />}
            title={picGroupName}
          />
          <ul className="mt-[6px] space-y-[3px] pl-1">
            {categories.map((category) => (
              <li key={category.name} className="flex items-start gap-2 text-[13px] leading-[17px]">
                <Bullet />
                {category.id && onCategoryClick ? (
                  <DiagramLink onClick={() => onCategoryClick(category.id!)}>
                    {category.name}
                  </DiagramLink>
                ) : (
                  <span style={{ color: "#332E42" }}>{category.name}</span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Tinted sub-section, per the unification mock: the provenance reads as a footnote to
            the categories above it rather than a second list of equal weight. */}
        <DiagramSubSection>
          <CardHeader
            icon={<ArrowPathIcon className="h-[16px] w-[16px]" />}
            title="Sourced From:"
          />
          <ul className="mt-[6px] space-y-[4px] pl-1">
            {sources.map((source) => (
              <li key={source.name} className="flex items-center gap-2 text-[13px] leading-[17px]">
                {source.logoUrl ? (
                  <img
                    src={source.logoUrl}
                    alt=""
                    className="h-4 w-4 shrink-0 rounded-sm object-contain"
                  />
                ) : (
                  <span
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm text-[10px] font-medium"
                    style={{ backgroundColor: "#EEEDFE", color: "#3C3489" }}
                  >
                    {source.name[0]}
                  </span>
                )}
                <span style={{ color: "#332E42" }}>{source.name}</span>
              </li>
            ))}
          </ul>
        </DiagramSubSection>
      </DiagramCard>

      <FlowConnector direction="out" />

      {/* Center card: the vendors that receive this group, by category */}
      <DiagramCard
        className={clsx("min-w-0", showPurposesColumn ? "flex-1" : "shrink-0")}
        style={showPurposesColumn ? undefined : { width: "calc(100% - 636px)" }}
      >
        <div className="p-[16px]">
          <CardHeader
            icon={<BuildingOffice2Icon className="h-[16px] w-[16px]" />}
            title="Processing Vendors"
          />
          {/* CSS columns pack the category groups top-to-bottom per column, so each column's
              groups sit flush — the old 3-col grid row-aligned them, opening a hole under a
              short group whenever its row neighbor ran long (crit c_3fdc53). */}
          <div className="mt-[8px]" style={{ columns: 3, columnGap: 28 }}>
            {sortedGroupNames.map((groupName) => (
              <div key={groupName} style={{ breakInside: "avoid", marginBottom: 16 }}>
                <h4 className="mb-[4px] text-[12px] font-semibold" style={{ color: "#534E65" }}>
                  {groupName}
                </h4>
                <ul className="space-y-[4px]">
                  {groupedVendors[groupName].map((vendor) => (
                    <li
                      key={vendor.id}
                      className="flex items-center gap-2 text-[13px] leading-[17px] min-w-0"
                    >
                      <DataRecipientLogo
                        dataRecipient={vendor}
                        size={16}
                        className="shrink-0 rounded-sm"
                      />
                      {onVendorClick ? (
                        <DiagramLink
                          onClick={() => onVendorClick(vendor.vendorId)}
                          className="truncate"
                        >
                          {vendor.name}
                        </DiagramLink>
                      ) : (
                        <span className="truncate" style={{ color: "#332E42" }}>
                          {vendor.name}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </DiagramCard>

      {showPurposesColumn && (
        <>
          <FlowConnector direction="in" />

          {/* Right card: why the data is processed, and the GDPR bases when they apply */}
          <DiagramCard style={{ width: 280 }} className="shrink-0 self-center">
            {processingPurposes.length > 0 && (
              <div className="p-[16px]">
                <CardHeader
                  icon={<CogIcon className="h-[16px] w-[16px]" />}
                  title="Processing Purposes"
                />
                <ul className="mt-[6px] space-y-[3px] pl-1">
                  {processingPurposes.map((purpose) => (
                    <li
                      key={purpose.id}
                      className="flex items-start gap-2 text-[13px] leading-[17px]"
                    >
                      <Bullet />
                      {onPurposeClick ? (
                        <DiagramLink onClick={() => onPurposeClick(purpose.id)}>
                          {purpose.name}
                        </DiagramLink>
                      ) : (
                        <span style={{ color: "#332E42" }}>{purpose.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {showLawfulBases && (
              <DiagramSubSection>
                <CardHeader
                  icon={<ShieldCheckIcon className="h-[16px] w-[16px]" />}
                  title="Lawful Bases (GDPR):"
                />
                <ul className="mt-[6px] space-y-[3px] pl-1">
                  {lawfulBases.map((basis) => (
                    <li key={basis} className="flex items-start gap-2 text-[13px] leading-[17px]">
                      <Bullet />
                      <span style={{ color: "#332E42" }}>{labelForLawfulBasis(basis)}</span>
                    </li>
                  ))}
                </ul>
              </DiagramSubSection>
            )}
          </DiagramCard>
        </>
      )}
    </div>
  );
}

/**
 * Interactive text inside the diagram: always underlined, purple on hover (crit c_6ae494).
 */
function DiagramLink({
  onClick,
  children,
  className,
}: {
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "text-left cursor-pointer bg-transparent border-none p-0",
        "underline text-neutral-800 hover:text-primary-600",
        className,
      )}
      style={{ font: "inherit" }}
    >
      {children}
    </button>
  );
}

function Bullet() {
  return (
    <span
      className="mt-[7px] h-1 w-1 shrink-0 rounded-full"
      style={{ backgroundColor: "#918CA1" }}
    />
  );
}

function DiagramCard({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  // A 1px gradient frame (padding trick — border-image can't round corners) gives the cards the
  // florafauna-style gradient stroke outline (crit c_3fdc53).
  return (
    <div
      className={clsx("rounded-[12px]", className)}
      style={{
        padding: 1,
        background: "linear-gradient(135deg, #C4BBF1 0%, #E8E5EE 40%, #E8E5EE 60%, #C4BBF1 100%)",
        boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
        ...style,
      }}
    >
      <div className="overflow-hidden h-full" style={{ borderRadius: 11, backgroundColor: "#fff" }}>
        {children}
      </div>
    </div>
  );
}

/** Full-bleed tinted block at the foot of a diagram card. */
function DiagramSubSection({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{ padding: "10px 12px", backgroundColor: "#F5F4F9", borderTop: "1px solid #E8E5EE" }}
    >
      {children}
    </div>
  );
}

function CardHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <span style={{ color: "#534E65" }}>{icon}</span>
      <h3 className="text-[14px] font-semibold leading-[18px]" style={{ color: "#1A1726" }}>
        {title}
      </h3>
    </div>
  );
}

/** Stable per-mount SVG gradient ids — Date/random-free so renders stay deterministic. */
let flowConnectorIdCounter = 0;

/**
 * A small fan of bezier threads with a purple gradient stroke, after the florafauna-style
 * reference (crit c_3fdc53). `direction="out"` fans one thread into three (categories spreading
 * to many vendors); `"in"` converges three into one (vendors funneling into purposes).
 */
function FlowConnector({ direction }: { direction: "out" | "in" }) {
  const [gradientId] = React.useState(() => `df-flow-connector-${flowConnectorIdCounter++}`);
  const paths =
    direction === "out"
      ? [
          "M0 70 C 20 70, 28 26, 48 26",
          "M0 70 C 20 70, 28 70, 48 70",
          "M0 70 C 20 70, 28 114, 48 114",
        ]
      : [
          "M0 26 C 20 26, 28 70, 48 70",
          "M0 70 C 20 70, 28 70, 48 70",
          "M0 114 C 20 114, 28 70, 48 70",
        ];

  return (
    <div className="flex shrink-0 self-center items-center" style={{ width: 48 }}>
      <svg width="48" height="140" viewBox="0 0 48 140" fill="none" aria-hidden="true">
        <defs>
          <linearGradient
            id={gradientId}
            x1="0"
            y1="70"
            x2="48"
            y2="70"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#C4BBF1" />
            <stop offset="0.5" stopColor="#9C8FE9" />
            <stop offset="1" stopColor="#C4BBF1" />
          </linearGradient>
        </defs>
        {paths.map((d, i) => (
          <path
            key={d}
            d={d}
            stroke={`url(#${gradientId})`}
            strokeWidth="1.25"
            strokeLinecap="round"
            opacity={i === 1 ? 0.95 : 0.6}
          />
        ))}
      </svg>
    </div>
  );
}
